import type { DefectStatus, DefectSummary, QuestionSeverity } from '@my/check-api';

// ────────────────────────────────────────────────────────────────────────────
// Presentation layer for defects.
//
// The domain types come from @my/check-api, mirroring the BE DTOs. This file
// holds only what the BE has no opinion about: colour, ordering for display,
// and time formatting.
// ────────────────────────────────────────────────────────────────────────────

/** Statuses that keep a defect in the queue count. */
export const UNRESOLVED_STATUSES: DefectStatus[] = ['Open', 'InProgress', 'Deferred'];

export const SEVERITY_ORDER: QuestionSeverity[] = ['High', 'Medium', 'Low'];
export const STATUS_ORDER: DefectStatus[] = ['Open', 'InProgress', 'Deferred', 'Resolved'];

export function isUnresolved(d: Pick<DefectSummary, 'status'>): boolean {
  return UNRESOLVED_STATUSES.includes(d.status);
}

/** Left-edge stripe on a queue card — the fastest read on it. */
export const SEVERITY_STRIPE: Record<QuestionSeverity, string> = {
  High: 'bg-red-500',
  Medium: 'bg-amber-400',
  Low: 'bg-emerald-400',
};

export const SEVERITY_BADGE: Record<QuestionSeverity, string> = {
  High: 'border-red-200 bg-red-50 text-red-600',
  Medium: 'border-amber-200 bg-amber-50 text-amber-700',
  Low: 'border-emerald-200 bg-emerald-50 text-emerald-700',
};

export const STATUS_BADGE: Record<DefectStatus, string> = {
  Open: 'border-red-200 bg-white text-red-600',
  InProgress: 'border-blue-200 bg-blue-50 text-blue-600',
  Deferred: 'border-slate-300 bg-white text-slate-600',
  Resolved: 'border-slate-200 bg-slate-100 text-slate-500',
};

/**
 * Classify a timestamp for display. Returns the pieces rather than a finished
 * string so the caller can run them through i18n.
 */
export function describeWhen(
  iso: string,
  now: Date = new Date(),
): { kind: 'today' | 'yesterday' | 'older'; time: string; date: string } {
  const d = new Date(iso);
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  const date = d.toLocaleDateString();

  const midnight = new Date(now);
  midnight.setHours(0, 0, 0, 0);
  const yesterdayMidnight = new Date(midnight);
  yesterdayMidnight.setDate(yesterdayMidnight.getDate() - 1);

  if (d >= midnight) return { kind: 'today', time, date };
  if (d >= yesterdayMidnight) return { kind: 'yesterday', time, date };
  return { kind: 'older', time, date };
}

// ────────────────────────────────────────────────────────────────────────────
// Open questions still unresolved:
//
//  1. GARAGE IS FREE TEXT — the schema has GaragePartyId alongside GarageName,
//     but nothing fills it. Until a Directory Party picker replaces the text
//     box, "everything at Coyle Tyres" is a name match rather than a join.
//  2. NO PHOTOS ON THE DEFECT — the driver's photo lives on the CheckAnswer
//     that raised it. Reachable, but this screen does not show it.
//  3. VOR — severity is presentational. Nothing stops a High defect vehicle
//     being used, and nothing records that it was taken off the road.
//  4. NO MANUAL DEFECT — every defect comes from a failed check answer. There
//     is no way to raise one for a fault spotted outside a walk-around, even
//     though Defect.CheckAnswerId is nullable to allow it.
//  5. NO REJECTION PATH — "inspected, no fault found" has to be recorded as a
//     Resolved action with a note, which overstates what happened.
// ────────────────────────────────────────────────────────────────────────────
